import { useState, useEffect, useMemo } from 'react';
import Modal from './Modal';
import { getLocalDateString } from '../utils/dateUtils';
import { useUsers } from '../hooks/useUsers';
import { useAuth } from '../hooks/useAuth';

export default function ActionItemModal({ isOpen, onClose, onSave, editItem = null, relationshipPersonName = null }) {
  const { user } = useAuth();
  const { supervisors } = useUsers();

  // Build list of assignable users: current user + supervisors + 1:1 person
  const assignableUsers = useMemo(() => {
    const users = [];
    if (user) {
      users.push({
        uid: user.uid,
        displayName: user.displayName || 'Me',
        firstName: user.displayName?.split(' ')[0] || 'Me',
        isExternal: false
      });
    }
    supervisors.forEach(s => {
      users.push({
        uid: s.uid,
        displayName: s.displayName,
        firstName: s.firstName || s.displayName?.split(' ')[0] || 'User',
        isExternal: false
      });
    });
    // Add the 1:1 relationship person if provided
    if (relationshipPersonName) {
      users.push({
        uid: `external:${relationshipPersonName}`,
        displayName: relationshipPersonName,
        firstName: relationshipPersonName,
        isExternal: true
      });
    }
    return users;
  }, [user, supervisors, relationshipPersonName]);

  const [formData, setFormData] = useState({
    text: '',
    assignedToUid: '',
    dueDate: '',
    followUpDate: ''
  });

  useEffect(() => {
    if (editItem) {
      setFormData({
        text: editItem.text || '',
        assignedToUid: editItem.assignedToUid || editItem.assignedTo || user?.uid || '',
        dueDate: editItem.dueDate || '',
        followUpDate: editItem.followUpDate || ''
      });
    } else {
      // Default due date to end of current week (Friday)
      const today = new Date();
      const dayOfWeek = today.getDay();
      const daysUntilFriday = dayOfWeek <= 5 ? 5 - dayOfWeek : 5 + (7 - dayOfWeek);
      const friday = new Date(today);
      friday.setDate(today.getDate() + daysUntilFriday);

      setFormData({
        text: '',
        assignedToUid: user?.uid || '',
        dueDate: getLocalDateString(friday),
        followUpDate: ''
      });
    }
  }, [editItem, isOpen, user?.uid]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.text.trim()) return;

    // Find the assigned user to get their display name
    const assignedUser = assignableUsers.find(u => u.uid === formData.assignedToUid);

    onSave({
      ...formData,
      assignedTo: assignedUser?.firstName || assignedUser?.displayName || 'Unknown',
      isExternalAssignee: assignedUser?.isExternal || false
    });
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={editItem ? 'Edit Action Item' : 'Add Action Item'}
    >
      <form onSubmit={handleSubmit} className="flex flex-col gap-4">
        <div>
          <label className="block text-[13px] font-medium mb-1.5 text-text">
            Action Item
          </label>
          <textarea
            placeholder="What needs to be done?"
            value={formData.text}
            onChange={(e) => setFormData({ ...formData, text: e.target.value })}
            required
            rows={3}
            className="w-full py-2.5 px-3 border border-border rounded-md text-sm outline-none focus:border-orange resize-none"
          />
        </div>

        <div>
          <label className="block text-[13px] font-medium mb-1.5 text-text">
            Assigned To
          </label>
          <select
            value={formData.assignedToUid}
            onChange={(e) => setFormData({ ...formData, assignedToUid: e.target.value })}
            className="w-full py-2.5 px-3 border border-border rounded-md text-sm outline-none bg-white focus:border-orange"
          >
            {assignableUsers.map((userObj) => (
              <option key={userObj.uid} value={userObj.uid}>
                {userObj.firstName}
              </option>
            ))}
          </select>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-[13px] font-medium mb-1.5 text-text">
              Due Date
            </label>
            <input
              type="date"
              value={formData.dueDate}
              onChange={(e) => setFormData({ ...formData, dueDate: e.target.value })}
              required
              className="w-full py-2.5 px-3 border border-border rounded-md text-sm outline-none focus:border-orange"
            />
          </div>

          <div>
            <label className="block text-[13px] font-medium mb-1.5 text-text">
              Follow-up Date
            </label>
            <input
              type="date"
              value={formData.followUpDate}
              onChange={(e) => setFormData({ ...formData, followUpDate: e.target.value })}
              className="w-full py-2.5 px-3 border border-border rounded-md text-sm outline-none focus:border-orange"
            />
          </div>
        </div>

        <div className="flex gap-3 mt-2">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 py-3 px-5 bg-grey-light text-text border-none rounded-md text-sm font-medium cursor-pointer hover:bg-border"
          >
            Cancel
          </button>
          <button
            type="submit"
            className="flex-1 py-3 px-5 bg-orange text-white border-none rounded-md text-sm font-medium cursor-pointer hover:bg-orange-hover"
          >
            {editItem ? 'Save Changes' : 'Add Action Item'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
